import React from 'react';
import { Box, Button, Flex, IconButton, Select, Switch, Text } from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';

import {
  CountryCitySelector,
  FormField,
  MultiSelect,
  QuillEditor,
} from 'shared/components';
import { AutocompleteV2 } from 'shared/components/autocomplete-v2';
import { AddIcon, ClearIcon, SearchIcon } from 'shared/icons';

import { useCreateResumeContext } from '../create-resume.context';
import { getRequiredFieldError } from '../create-resume.validation';

type WorkExperienceProps = {
  renderSteps: (valid: boolean) => React.ReactNode;
  showErrors: boolean;
};

const WorkExperienceView = ({ renderSteps, showErrors }: WorkExperienceProps) => {
  const { workExperience } = useCreateResumeContext();

  return (
    <form>
      <Box>
        <Flex direction="column" gap="5" my="4">
          {workExperience.experiences.map((experience) => (
            <Box key={experience.id} p="4" style={{ border: '1px solid var(--gray-a6)', borderRadius: 8 }}>
              <Flex direction="column" gap="4">
                <Flex justify="between" align="center">
                  <Text weight="medium">Досвід роботи</Text>
                  <IconButton
                    type="button"
                    variant="soft"
                    color="red"
                    onClick={() => {
                      workExperience.removeExperience(experience.id);
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                </Flex>

                <FormField
                  label="Посада"
                  required
                  error={getRequiredFieldError(showErrors, Boolean(experience.position.trim()))}
                >
                  <AutocompleteV2.Root
                    items={experience.positionAutocompleteOptions}
                    viewModel={experience.positionVM}
                    getOptionValue={(position) => position.id}
                    getOptionLabel={(position) => position.name}
                    color="indigo"
                    indicator={<SearchIcon width={20} height={20} />}
                    rotateIndicatorOnOpen={false}
                    loading={experience.isPositionLoading}
                    placeholder="Введіть посаду"
                    resetInputOnClose="never"
                    onSearch={experience.onSearchPosition}
                    onSelect={experience.onSelectPosition}
                  />
                </FormField>

                <FormField
                  label="Компанія"
                  required
                  error={getRequiredFieldError(
                    showErrors,
                    Boolean(experience.organization.trim())
                  )}
                >
                  <AutocompleteV2.Root
                    items={experience.organizationAutocompleteOptions}
                    viewModel={experience.organizationVM}
                    getOptionValue={(organization) => organization.id}
                    getOptionLabel={(organization) => organization.name}
                    color="indigo"
                    indicator={<SearchIcon width={20} height={20} />}
                    rotateIndicatorOnOpen={false}
                    loading={experience.isOrganizationLoading}
                    placeholder="Введіть компанію"
                    resetInputOnClose="never"
                    onSearch={experience.onSearchOrganization}
                    onSelect={experience.onSelectOrganization}
                  />
                </FormField>

                <FormField
                  label="Сфера"
                  required
                  error={getRequiredFieldError(
                    showErrors,
                    experience.industries.values.length > 0
                  )}
                >
                  <MultiSelect.Root
                    vm={experience.industries}
                    placeholder="Оберіть сферу"
                  >
                    {workExperience.filteredIndustries(experience).map((industry) => (
                      <MultiSelect.Option key={industry.id} value={industry}>
                        {industry.name}
                      </MultiSelect.Option>
                    ))}
                  </MultiSelect.Root>
                </FormField>

                <CountryCitySelector
                  store={experience.location}
                  countries={workExperience.countries}
                  requiredCountry
                  countryError={getRequiredFieldError(
                    showErrors,
                    Boolean(experience.expCountry.trim())
                  )}
                />

                <Flex gap="4">
                  <Flex direction="column" gap="2" width="100%">
                    <Text size="2">Початок роботи</Text>
                    <Flex gap="3">
                      <FormField
                        label="Рік"
                        required
                        error={
                          getRequiredFieldError(
                            showErrors,
                            Boolean(experience.fromYear.trim())
                          ) ??
                          experience.dateYearRangeError
                        }
                      >
                        <Select.Root
                          size="3"
                          value={experience.fromYear}
                          onValueChange={(value) => {
                            workExperience.updateField(
                              experience.id,
                              'fromYear',
                              value
                            );
                          }}
                        >
                          <Select.Trigger color="indigo" placeholder="Оберіть рік" />
                          <Select.Content color="indigo" position="popper">
                            {workExperience.years.map((year) => (
                              <Select.Item key={year} value={String(year)}>
                                {year}
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Root>
                      </FormField>

                      <FormField
                        label="Місяць"
                        required
                        error={
                          getRequiredFieldError(
                            showErrors,
                            Boolean(experience.fromMonth.trim())
                          ) ??
                          experience.dateMonthRangeError
                        }
                      >
                        <Select.Root
                          size="3"
                          value={experience.fromMonth}
                          onValueChange={(value) => {
                            workExperience.updateField(
                              experience.id,
                              'fromMonth',
                              value
                            );
                          }}
                        >
                          <Select.Trigger color="indigo" placeholder="Оберіть місяць" />
                          <Select.Content color="indigo" position="popper">
                            {workExperience.months.map((month) => (
                              <Select.Item key={month.id} value={String(month.id)}>
                                {month.name}
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Root>
                      </FormField>
                    </Flex>
                  </Flex>

                  <Flex direction="column" gap="2" width="100%">
                    <Text size="2">Кінець роботи</Text>
                    <Flex gap="3">
                      <FormField
                        label="Рік"
                        required={!experience.stillWorking}
                        error={
                          getRequiredFieldError(
                            showErrors,
                            experience.stillWorking || Boolean(experience.toYear.trim())
                          )
                        }
                      >
                        <Select.Root
                          size="3"
                          disabled={experience.stillWorking}
                          value={experience.toYear}
                          onValueChange={(value) => {
                            workExperience.updateField(
                              experience.id,
                              'toYear',
                              value
                            );
                          }}
                        >
                          <Select.Trigger color="indigo" placeholder="Оберіть рік" />
                          <Select.Content color="indigo" position="popper">
                            {workExperience.years.map((year) => (
                              <Select.Item key={year} value={String(year)}>
                                {year}
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Root>
                      </FormField>

                      <FormField
                        label="Місяць"
                        required={!experience.stillWorking}
                        error={getRequiredFieldError(
                          showErrors,
                          experience.stillWorking || Boolean(experience.toMonth.trim())
                        )}
                      >
                        <Select.Root
                          size="3"
                          disabled={experience.stillWorking}
                          value={experience.toMonth}
                          onValueChange={(value) => {
                            workExperience.updateField(
                              experience.id,
                              'toMonth',
                              value
                            );
                          }}
                        >
                          <Select.Trigger color="indigo" placeholder="Оберіть місяць" />
                          <Select.Content color="indigo" position="popper">
                            {workExperience.months.map((month) => (
                              <Select.Item key={month.id} value={String(month.id)}>
                                {month.name}
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Root>
                      </FormField>
                    </Flex>
                  </Flex>
                </Flex>

                <Flex align="center" gap="2">
                  <Switch
                    checked={experience.stillWorking}
                    onCheckedChange={(checked) => {
                      workExperience.updateField(experience.id, 'stillWorking', checked);
                    }}
                  />
                  <Text size="2">Працюю тут зараз</Text>
                </Flex>

                <FormField
                  label="Досягнення"
                  required
                  error={getRequiredFieldError(
                    showErrors,
                    Boolean(experience.achievements.trim())
                  )}
                >
                  <QuillEditor
                    value={experience.achievements}
                    minHeight={120}
                    onValueChange={(value) => {
                      workExperience.updateField(
                        experience.id,
                        'achievements',
                        value
                      );
                    }}
                  />
                </FormField>
              </Flex>
            </Box>
          ))}

          <Button
            type="button"
            variant="outline"
            onClick={() => {
              workExperience.addExperience();
            }}
          >
            Додати досвід <AddIcon />
          </Button>
        </Flex>
      </Box>

      <Flex mt="4">{renderSteps(workExperience.valid)}</Flex>
    </form>
  );
};

export const WorkExperience = observer(WorkExperienceView);
